/**
 * KpaUsers - Gestion des utilisateurs KPA Ton Concours
 * Colonnes kpa_* : crédits, recherches, interactions chatbot, onboarding, FCM token
 */
import {
  AlertCircle,
  CheckCircle,
  Coins,
  Loader2,
  MessageCircle,
  RefreshCw,
  Search,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { authFetch } from '../../services/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface KpaUser {
  id: string;
  email?: string;
  phone_number?: string;
  display_name?: string;
  kpa_credits: number;
  kpa_free_searches_used: number;
  kpa_total_searches: number;
  kpa_chatbot_interactions: number;
  kpa_onboarding_completed: boolean;
  kpa_preferred_categories: string[];
  kpa_fcm_token?: string;
  created_at: string;
  last_login_at?: string;
}

const KpaUsers: React.FC = () => {
  const [users, setUsers] = useState<KpaUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState<KpaUser | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const limit = 20;

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        ...(search ? { search } : {}),
      });
      const res = await authFetch(`${API_BASE_URL}/api/pka/users?${params}`);
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const data = await res.json();
      setUsers(data.users ?? []);
      setTotal(data.total ?? 0);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const addCredits = async (userId: string, amount: number) => {
    setActionLoading(userId);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/pka/users/${userId}/credits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, reason: 'manuel_admin' }),
      });
      if (!res.ok) throw new Error('Erreur lors de l\'ajout de crédits');
      setMessage({ type: 'success', text: `${amount} crédits ajoutés` });
      loadUsers();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const formatDate = (d?: string) =>
    d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const displayName = (u: KpaUser) =>
    u.display_name || u.phone_number || u.email?.replace('@whatsapp.kpatonconcours.app', ' (WA)') || u.id.slice(0, 8);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-orange-500">🔍</span>
            <h1 className="text-2xl font-bold text-gray-900">Utilisateurs KPA</h1>
          </div>
          <p className="text-gray-500 text-sm">Crédits, recherches, chatbot IA — {total} utilisateurs au total</p>
        </div>
        <button onClick={loadUsers} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition">
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {message && (
        <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Rechercher par nom, email ou téléphone..."
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">
            <AlertCircle className="w-8 h-8 mx-auto mb-2" />
            <p>{error}</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Users className="w-8 h-8 mx-auto mb-2" />
            <p>Aucun utilisateur trouvé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-600 font-medium">Utilisateur</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">
                    <div className="flex items-center justify-center gap-1"><Coins className="w-3.5 h-3.5" /> Crédits</div>
                  </th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">
                    <div className="flex items-center justify-center gap-1"><Search className="w-3.5 h-3.5" /> Recherches</div>
                  </th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">
                    <div className="flex items-center justify-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> Chatbot</div>
                  </th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">
                    <div className="flex items-center justify-center gap-1"><UserCheck className="w-3.5 h-3.5" /> Onboarding</div>
                  </th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Push</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-medium">Inscrit</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{displayName(user)}</p>
                        <p className="text-xs text-gray-400 truncate max-w-[180px]">
                          {user.email?.includes('@whatsapp') ? '📱 WhatsApp' : user.email}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-bold ${user.kpa_credits > 0 ? 'text-yellow-600' : 'text-red-500'}`}>
                        {user.kpa_credits}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">
                      {user.kpa_total_searches}
                      <span className="text-xs text-gray-400"> ({user.kpa_free_searches_used} grat.)</span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">{user.kpa_chatbot_interactions}</td>
                    <td className="px-4 py-3 text-center">
                      {user.kpa_onboarding_completed
                        ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                        : <X className="w-4 h-4 text-gray-300 mx-auto" />}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {user.kpa_fcm_token
                        ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                        : <X className="w-4 h-4 text-gray-300 mx-auto" />}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">{formatDate(user.created_at)}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => setSelectedUser(user)}
                          className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded text-gray-600 transition"
                        >
                          Détail
                        </button>
                        <button
                          onClick={() => addCredits(user.id, 5)}
                          disabled={actionLoading === user.id}
                          className="px-2 py-1 text-xs bg-yellow-100 hover:bg-yellow-200 rounded text-yellow-700 transition flex items-center gap-1"
                          title="+5 crédits"
                        >
                          {actionLoading === user.id ? <Loader2 className="w-3 h-3 animate-spin" /> : '+5 💰'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-500">
              Page {page}/{totalPages} — {total} utilisateurs
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40"
              >
                Préc.
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40"
              >
                Suiv.
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User detail modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-bold text-gray-900">Détail utilisateur KPA</h2>
              <button onClick={() => setSelectedUser(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <InfoItem label="Nom" value={displayName(selectedUser)} />
                <InfoItem label="Email" value={selectedUser.email || '—'} />
                <InfoItem label="Téléphone" value={selectedUser.phone_number || '—'} />
                <InfoItem label="Inscrit" value={formatDate(selectedUser.created_at)} />
                <InfoItem label="Crédits KPA" value={String(selectedUser.kpa_credits)} highlight />
                <InfoItem label="Recherches gratuites" value={`${selectedUser.kpa_free_searches_used}/3`} />
                <InfoItem label="Total recherches" value={String(selectedUser.kpa_total_searches)} />
                <InfoItem label="Interactions chatbot" value={String(selectedUser.kpa_chatbot_interactions)} />
                <InfoItem label="Onboarding" value={selectedUser.kpa_onboarding_completed ? '✅ Terminé' : '❌ Incomplet'} />
                <InfoItem label="Push actif" value={selectedUser.kpa_fcm_token ? '✅ Oui' : '❌ Non'} />
              </div>
              {selectedUser.kpa_preferred_categories?.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Catégories préférées</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedUser.kpa_preferred_categories.map(c => (
                      <span key={c} className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs">{c}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => { addCredits(selectedUser.id, 5); setSelectedUser(null); }}
                  className="flex-1 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-sm font-medium transition"
                >
                  +5 crédits
                </button>
                <button
                  onClick={() => { addCredits(selectedUser.id, 20); setSelectedUser(null); }}
                  className="flex-1 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition"
                >
                  +20 crédits
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoItem: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="bg-gray-50 rounded-lg p-3">
    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
    <p className={`text-sm font-medium ${highlight ? 'text-orange-600' : 'text-gray-900'}`}>{value}</p>
  </div>
);

export default KpaUsers;
